import React, { useEffect } from 'react';
import { Control, Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  GeneralSelect,
  OptionType,
} from 'v2/form/CustomSelectors/GeneralSelect';
import { UserInviteTeamMemberForm } from '../../InviteTeamMemberModal';
import styles from './InviteTeamMemberFormFields.module.scss';
import Select, { MultiValue, components } from 'react-select';
import { MultiDropdownItem, Organization } from 'v2/models';
import {
  useStrapiUserEmailValidation,
  useUserInviteEmailValidation,
} from 'v2/queries';
import AlertError from 'v2/common/alerts/AlertError/AlertError';
import { useMultiSelectLicensesOptions } from 'v2/hooks/useMultiSelectLicenses';
import * as yup from 'yup';
import { CurrentStateFilterContext } from 'v2/context/currentStateFilterContext';
import { FormControl } from 'react-bootstrap';
import { getMultiSelectStyles } from 'v2/form/CustomSelectors/Multiselect/Styles/SelectStyles';
import OptionComponent from 'v2/form/CustomSelectors/Multiselect/ComplementaryComponents/OptionComponent';
import { SelectOption } from 'v2/form/CustomSelectors/Multiselect/MultiFilterDropdown';
import classnames from 'classnames';

type InviteTeamMemberFormFieldsLicensesProps = {
  organizations: Organization[];
  showOrganizationDropdown: boolean;
  control: Control<UserInviteTeamMemberForm>;
  index: number;
  selectedOption?: OptionType;
};

const InviteTeamMemberFormFieldsLicenses = ({
  organizations,
  showOrganizationDropdown,
  control,
  index,
  selectedOption,
}: InviteTeamMemberFormFieldsLicensesProps) => {
  const bulkInvitations = useWatch({ control, name: 'bulkInvitations' });
  const {
    setValue,
    register,
    formState: { errors },
  } = useFormContext<UserInviteTeamMemberForm>();

  const enteredEmail = bulkInvitations[index].inputEmails.emails[0];
  const allEmails = bulkInvitations
    .filter((_, i) => i !== index)
    .map((invitation) => invitation.inputEmails.emails[0]);

  const {
    mutate: checkUserInviteEmail,
    error: userInviteEmailValidationError,
    reset: resetUserInviteEmailValidation,
  } = useUserInviteEmailValidation();

  const {
    mutate: checkStrapiUserEmail,
    error: strapiUserEmailValidationError,
    reset: resetStrapiUserEmailValidation,
  } = useStrapiUserEmailValidation();

  useEffect(() => {
    resetStrapiUserEmailValidation();
    resetUserInviteEmailValidation();
    if (yup.string().email().required().isValidSync(enteredEmail)) {
      checkStrapiUserEmail(enteredEmail);
      checkUserInviteEmail(enteredEmail);
    }
  }, [enteredEmail]);

  const emailError = errors.bulkInvitations
    ? errors.bulkInvitations[index]?.inputEmails?.emails?.[0]?.message
    : '';

  const error: string =
    strapiUserEmailValidationError ??
    userInviteEmailValidationError ??
    emailError ??
    '';

  const errorNotEmpty = error !== '';

  const { options, currentSelection, setCurrentSelection } =
    useMultiSelectLicensesOptions(bulkInvitations);

  const handleMultiSelectChange = (
    newSelectedItems: MultiValue<SelectOption<MultiDropdownItem>>
  ) => {
    const idCounter: Record<string, number> = {};
    newSelectedItems.forEach((item) => {
      idCounter[(item.value as MultiDropdownItem).id] =
        (idCounter[(item.value as MultiDropdownItem).id] || 0) + 1;
    });
    const newArray = newSelectedItems.filter((item) => {
      return idCounter[(item.value as MultiDropdownItem).id] === 1;
    });
    const newElements = newArray.map(
      (element) => element.value
    ) as unknown as MultiDropdownItem[];
    setCurrentSelection((previous) => {
      return {
        ...previous,
        list: newElements,
      };
    });
    const fieldElements = newArray.map((element) => {
      return {
        label: element.label,
        value: element.value,
        isDisabled: element.value.isDisabled,
      };
    });
    setValue(`bulkInvitations.${index}.licenses`, [...fieldElements]);
  };

  const dropdownSelectValue = bulkInvitations[index].licenses?.map((e) => {
    return {
      value: {
        name: e.label,
        id: e.value.id,
        isDisabled: e.isDisabled,
        tooltip: e.value.tooltip,
      },
      label: e.label,
    };
  });

  return (
    <div className={styles.inputs}>
      {showOrganizationDropdown && (
        <Controller
          control={control}
          name={`bulkInvitations.${index}.organization`}
          render={({ field }) => (
            <GeneralSelect
              aria-label="Organization"
              selectedOption={selectedOption}
              options={organizations.map(({ id, name }) => ({
                label: name,
                value: id,
              }))}
              handleChange={(option) => option && field.onChange(option.value)}
              components={{
                DropdownIndicator:
                  organizations.length === 0
                    ? null
                    : components.DropdownIndicator,
              }}
              menuIsOpen={organizations.length === 0 ? false : undefined}
              fullWidth
            />
          )}
        />
      )}

      <div>
        <FormControl
          type="text"
          placeholder="Email Address"
          aria-label="email-input"
          autoComplete="off"
          maxLength={255}
          {...register(`bulkInvitations.${index}.inputEmails.emails.0`, {
            required: true,
            validate: {
              format: (value) => yup.string().email().isValidSync(value),
              duplicate: (value) =>
                !allEmails.includes(value) || 'Email duplicated',
            },
          })}
        />
        <div
          className={classnames({ [styles.errorsContainer]: errorNotEmpty })}
        >
          {error && <AlertError message={error} />}
        </div>
      </div>
      <div>
        <Controller
          control={control}
          name={`bulkInvitations.${index}.licenses`}
          render={({ field: { onChange } }) => (
            <CurrentStateFilterContext.Provider
              value={{
                setCurrentState: setCurrentSelection,
                currentState: currentSelection,
              }}
            >
              <Select<SelectOption<MultiDropdownItem>, true>
                onChange={(selectedOptions) => {
                  onChange(selectedOptions);
                  handleMultiSelectChange(selectedOptions);
                }}
                options={options}
                isMulti
                backspaceRemovesValue={false}
                hideSelectedOptions={false}
                closeMenuOnSelect={false}
                isClearable={false}
                styles={getMultiSelectStyles()}
                placeholder={'Select a License'}
                components={{
                  ...components,
                  IndicatorSeparator: null,
                  Option: OptionComponent,
                }}
                value={dropdownSelectValue}
              />
            </CurrentStateFilterContext.Provider>
          )}
        ></Controller>
      </div>
    </div>
  );
};

export default InviteTeamMemberFormFieldsLicenses;
